/**
 * Admin Session Manager for Cross-Tab Synchronization
 * Handles admin authentication state across multiple browser tabs
 */

import { toast } from 'sonner';
import { clearSessionData } from '@/lib/security';
import config from '@/config';

export interface CrossTabMessage {
  type: 'ADMIN_LOGOUT' | 'ADMIN_SESSION_REFRESHED' | 'ADMIN_SESSION_WARNING' | 'ADMIN_SESSION_EXPIRED';
  data?: any;
  timestamp: number;
  tabId: string;
}

export class AdminSessionManager {
  private broadcastChannel: BroadcastChannel | null = null;
  private tabId: string;
  private isInitialized = false;

  constructor() {
    this.tabId = this.generateTabId();
    this.initializeCrossTabSync();
  }

  private generateTabId(): string {
    return `admin-tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private initializeCrossTabSync(): void {
    try {
      // Check if BroadcastChannel is supported
      if (typeof BroadcastChannel !== 'undefined') {
        this.broadcastChannel = new BroadcastChannel('admin-auth');
        this.setupMessageHandlers();
        this.isInitialized = true;
        
        if (config.enableDebugLogging) {
          console.log('🔗 Cross-tab sync initialized for admin session');
        }
      } else {
        console.warn('BroadcastChannel not supported, using localStorage fallback');
        this.setupLocalStorageFallback();
        this.isInitialized = true;
      }
    } catch (error) {
      console.error('Failed to initialize cross-tab sync:', error);
      this.setupLocalStorageFallback();
      this.isInitialized = true;
    }
  }

  private setupMessageHandlers(): void {
    if (!this.broadcastChannel) return;

    this.broadcastChannel.addEventListener('message', (event: MessageEvent<CrossTabMessage>) => {
      const { type, data, timestamp, tabId } = event.data;
      
      // Ignore messages from the same tab
      if (tabId === this.tabId) return;
      
      // Ignore old messages (older than 5 seconds)
      if (Date.now() - timestamp > 5000) return;

      if (config.enableDebugLogging) {
        console.log('📨 Cross-tab message received:', { type, tabId, timestamp });
      }

      switch (type) {
        case 'ADMIN_LOGOUT':
          this.handleCrossTabLogout();
          break;
        case 'ADMIN_SESSION_REFRESHED':
          this.handleCrossTabRefresh(data);
          break;
        case 'ADMIN_SESSION_WARNING':
          this.handleCrossTabWarning(data);
          break;
        case 'ADMIN_SESSION_EXPIRED':
          this.handleCrossTabSessionExpired();
          break;
      }
    });
  }

  private setupLocalStorageFallback(): void {
    // Fallback to localStorage events for older browsers
    window.addEventListener('storage', (event) => {
      if (event.key === 'admin-auth-sync' && event.newValue) {
        try {
          const message: CrossTabMessage = JSON.parse(event.newValue);
          if (message.tabId !== this.tabId) {
            this.handleCrossTabMessage(message);
          }
        } catch (error) {
          console.error('Failed to parse localStorage sync message:', error);
        }
      }
    });
  }

  private handleCrossTabMessage(message: CrossTabMessage): void {
    const { type, data } = message;
    
    switch (type) {
      case 'ADMIN_LOGOUT':
        this.handleCrossTabLogout();
        break;
      case 'ADMIN_SESSION_REFRESHED':
        this.handleCrossTabRefresh(data);
        break;
      case 'ADMIN_SESSION_WARNING':
        this.handleCrossTabWarning(data);
        break;
      case 'ADMIN_SESSION_EXPIRED':
        this.handleCrossTabSessionExpired();
        break;
    }
  }

  private handleCrossTabLogout(): void {
    if (config.enableDebugLogging) {
      console.log('🚪 Cross-tab logout detected, clearing admin session');
    }

    // Clear admin session data
    clearSessionData();
    
    // Show admin-specific logout message
    toast.info('Admin session ended in another tab', {
      description: 'You have been logged out due to logout in another tab',
      duration: 5000
    });

    // Redirect to login after a short delay
    setTimeout(() => {
      window.location.href = '/login';
    }, 2000);
  }

  private handleCrossTabRefresh(_data: any): void {
    if (config.enableDebugLogging) {
      console.log('🔄 Cross-tab session refresh detected');
    }

    // Update last activity to reflect the refresh
    localStorage.setItem('lastActivity', Date.now().toString());
    
    // Show subtle notification
    toast.success('Session refreshed', {
      description: 'Your admin session was refreshed in another tab',
      duration: 3000
    });
  }

  private handleCrossTabWarning(data: any): void {
    const { timeLeft, minutesLeft } = data;
    
    if (config.enableDebugLogging) {
      console.log('⚠️ Cross-tab session warning:', { timeLeft, minutesLeft });
    }

    // Show admin-specific warning
    toast.warning(`Admin session expires in ${minutesLeft} minute${minutesLeft > 1 ? 's' : ''}`, {
      description: 'Session warning from another tab - please save your work',
      duration: 10000
    });
  }

  private handleCrossTabSessionExpired(): void {
    if (config.enableDebugLogging) {
      console.log('⏰ Cross-tab session expired');
    }

    // Check if form is active before clearing data
    const isFormActive = localStorage.getItem('form-active') === 'true';
    const hasFormData = localStorage.getItem('admin-form-state-backup') || localStorage.getItem('program-draft');
    
    if (isFormActive && hasFormData) {
      console.log('Form is active with data, showing warning instead of immediate logout');
      toast.warning('Session expired in another tab', {
        description: 'Please save your work and refresh the page to continue',
        duration: 10000
      });
      return;
    }

    // Clear session data
    clearSessionData();
    
    // Show admin-specific expiry message
    toast.error('Admin session expired', {
      description: 'Your session expired in another tab - please log in again',
      duration: 5000
    });

    // Redirect to login
    setTimeout(() => {
      window.location.href = '/login';
    }, 3000);
  }

  // Public methods for notifying other tabs
  public notifyLogout(): void {
    if (!this.isInitialized) return;

    const message: CrossTabMessage = {
      type: 'ADMIN_LOGOUT',
      timestamp: Date.now(),
      tabId: this.tabId
    };

    this.broadcastMessage(message);
    
    if (config.enableDebugLogging) {
      console.log('📤 Notifying other tabs: Admin logout');
    }
  }

  public notifySessionRefresh(newTokenData?: any): void {
    if (!this.isInitialized) return;

    const message: CrossTabMessage = {
      type: 'ADMIN_SESSION_REFRESHED',
      data: newTokenData,
      timestamp: Date.now(),
      tabId: this.tabId
    };

    this.broadcastMessage(message);
    
    if (config.enableDebugLogging) {
      console.log('📤 Notifying other tabs: Session refreshed');
    }
  }

  public notifySessionWarning(timeLeft: number, minutesLeft: number): void {
    if (!this.isInitialized) return;

    const message: CrossTabMessage = {
      type: 'ADMIN_SESSION_WARNING',
      data: { timeLeft, minutesLeft },
      timestamp: Date.now(),
      tabId: this.tabId
    };

    this.broadcastMessage(message);
    
    if (config.enableDebugLogging) {
      console.log('📤 Notifying other tabs: Session warning', { timeLeft, minutesLeft });
    }
  }

  public notifySessionExpired(): void {
    if (!this.isInitialized) return;

    const message: CrossTabMessage = {
      type: 'ADMIN_SESSION_EXPIRED',
      timestamp: Date.now(),
      tabId: this.tabId
    };

    this.broadcastMessage(message);
    
    if (config.enableDebugLogging) {
      console.log('📤 Notifying other tabs: Session expired');
    }
  }

  private broadcastMessage(message: CrossTabMessage): void {
    if (this.broadcastChannel) {
      this.broadcastChannel.postMessage(message);
    } else {
      // Fallback to localStorage
      try {
        localStorage.setItem('admin-auth-sync', JSON.stringify(message));
        // Clear after a short delay to prevent storage buildup
        setTimeout(() => {
          localStorage.removeItem('admin-auth-sync');
        }, 1000);
      } catch (error) {
        console.error('Failed to broadcast message via localStorage:', error);
      }
    }
  }

  public destroy(): void {
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
      this.broadcastChannel = null;
    }
    this.isInitialized = false;
  }

  public isReady(): boolean {
    return this.isInitialized;
  }
}

// Singleton instance
let sessionManagerInstance: AdminSessionManager | null = null;

export function getAdminSessionManager(): AdminSessionManager {
  if (!sessionManagerInstance) {
    sessionManagerInstance = new AdminSessionManager();
  }
  return sessionManagerInstance;
}

export function destroyAdminSessionManager(): void {
  if (sessionManagerInstance) {
    sessionManagerInstance.destroy();
    sessionManagerInstance = null;
  }
}
